import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, KuecheGatebefund, KuecheLeuchtenstand } from '../api';
import { IconOk, IconWarnung, IconElektro } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import { ErrorState, LoadingState, StatusBadge } from '../muster';
import { useModellSchreibrecht } from '../rechte';

const KOPFTEXT = 'Das Vollständigkeits-Gate vor der Rückschreibung: jede technische '
  + 'Beobachtung geklärt, jedes Bauteil mit Zustand, beide Küchenmodelle gelesen — '
  + 'erst dann schreibt der Lauf Marker in das Hostmodell.';

/**
 * Fremdmodelle > Küche > Datenqualität und Revit-Synchronisation.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DAS GATE MACHT SICHTBAR, WARUM NICHT GESCHRIEBEN WIRD
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eine abgeschaltete Schaltfläche ohne Begründung ist die schlechteste Form
 * von Sicherheit: Sie verhindert den Fehler und erzeugt die Ratlosigkeit.
 * Deshalb steht hier jeder Blocker im Klartext.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE PRÜFUNG LIEGT NICHT HIER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Diese Seite zeigt den Befund; abgewiesen wird im Server. Eine Oberfläche
 * kann fehlerhaft sein, ein Aufruf kann von woanders kommen — und ein Marker,
 * der einmal im Modell steht, steht dort.
 */

interface Rahmen {
  api: ApiClient;
  projekt?: { id: string; modelle?: Modell[] };
}

interface Modell {
  id: string;
  name: string;
  isActive: boolean;
  revitVersion: number;
  rollen?: string[];
}

/** ExportKind.Kuechenrueckgabe — die Marker aus dem DB-Stand schreiben. */
const KUECHENRUECKGABE = 141;

const HOSTROLLE = 'fremdmodelle';

export function Kuechenqualitaet() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [gate, setGate] = useState<KuecheGatebefund | null>(null);
  const [leuchten, setLeuchten] = useState<KuecheLeuchtenstand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [profilId, setProfilId] = useState<string | null>(null);
  // TD-WR1: Den Lauf 141 (Kuechenrueckgabe) nimmt der Server seit S12 nur von ProjectAdmin und SystemAdmin an -
  // auch als Probelauf, denn er öffnet das Hostmodell. Für alle anderen entfällt der Knopf; false, solange die
  // Antwort fehlt oder scheitert.
  const darfSchreiben = useModellSchreibrecht(api);

  const modell = (projekt?.modelle ?? []).find(
    (m) => m.isActive && (m.rollen ?? []).includes(HOSTROLLE));

  const laden = useCallback(async () => {
    if (!projekt) return;
    try {
      const [g, l] = await Promise.all([
        api.kuecheGate(projekt.id),
        api.kuecheLeuchten(projekt.id),
      ]);
      setGate(g);
      setLeuchten(l);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projekt]);

  useEffect(() => { void laden(); }, [laden]);

  useEffect(() => {
    if (!projekt || !modell) return;
    api.profile(projekt.id, modell.revitVersion)
      .then((p) => setProfilId(p.flatMap((x) => x.versions)[0]?.id ?? null))
      .catch(() => setProfilId(null));
  }, [api, projekt, modell?.id, modell?.revitVersion]);

  const abgleichen = async () => {
    if (!projekt) return;
    setLaeuft('leuchten');
    try {
      const e = await api.kuecheLeuchtenAbgleichen(projekt.id);
      setMeldung(
        `${e.zugeordnet} von ${e.leuchten} Leuchte(n) mit der Elektroplanung `
        + `verbunden, ${e.fehltInElektro} ohne Gegenstück, ${e.mehrdeutig} `
        + `mehrdeutig. ${e.inLueftungsdecke} sitzen in einer Lüftungsdecke.`);
      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const probelaufUmschalten = async () => {
    if (!projekt || !gate) return;
    setLaeuft('probelauf');
    try {
      const e = await api.kuecheMarkerProbelauf(
        projekt.id, !gate.markerProbelauf);
      setMeldung(e.hinweis);
      await laden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const schreiben = async () => {
    if (!projekt || !modell || !profilId) return;
    setLaeuft('marker');
    try {
      const auftrag = await api.auftragAnlegen({
        projectId: projekt.id,
        exportProfileVersionId: profilId,
        modelIds: [modell.id],
        kinds: [KUECHENRUECKGABE],
      });
      setMeldung(gate?.markerProbelauf
        ? `Probelauf beauftragt: ${auftrag.jobNumber}. Der Lauf legt die `
          + 'Marker an, meldet was entstehen würde, und rollt zurück — im '
          + 'Modell ändert sich nichts. Er sagt insbesondere, ob die '
          + 'Markerfamilie im Hostmodell geladen ist.'
        : `Markerprojektion beauftragt: ${auftrag.jobNumber}. Je realer `
          + 'Küchenkomponente entsteht ein Marker — XY vom verknüpften '
          + 'Element, Unterkante Gxx_OKRB + 0,40 m.');
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  if (fehler) {
    return (
      <>
        <PageHeader titel="Datenqualität und Revit-Synchronisation"
          beschreibung={KOPFTEXT} />
        <ErrorState
          meldung="Der Stand des Vollständigkeits-Gates konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => void laden()}
        />
      </>
    );
  }
  if (!gate) {
    return (
      <>
        <PageHeader titel="Datenqualität und Revit-Synchronisation"
          beschreibung={KOPFTEXT} />
        <LoadingState text="Vollständigkeits-Gate wird geprüft …" zeilen={5} spalten={3} />
      </>
    );
  }

  // Ein Probelauf rollt zurueck und erzeugt keinen Marker. Ihn mitzusperren
  // hiesse, den ersten Lauf ueberhaupt zum Schreiblauf zu machen -- in ein
  // Modell, in dem noch nie ein Marker gestanden hat.
  const startbereit = modell !== undefined && profilId !== null
                   && (gate.bereit || gate.markerProbelauf);

  return (
    <div className="seite">
      <PageHeader
        titel="Datenqualität und Revit-Synchronisation"
        beschreibung={KOPFTEXT}
        meta={(
          <StatusBadge
            zustand={gate.bereit ? 'gut' : 'fehler'}
            text={gate.bereit ? gate.status : `${gate.blocker.length} Blocker`}
          />
        )}
        aktionen={darfSchreiben ? (
        <button
          type="button"
          className="m-knopf m-knopf-haupt"
          disabled={!startbereit || laeuft !== null}
          title={gate.markerProbelauf
            ? 'Probelauf: Der Lauf legt die Marker an, meldet was entstehen '
            + 'würde, und rollt zurück. Im Modell ändert sich nichts.'
            : gate.bereit
            ? 'Je realer Küchenkomponente einen Marker schreiben'
            : 'Das Vollständigkeits-Gate steht nicht — siehe Blocker unten. '
            + 'Der Server weist den Schreiblauf ab.'}
          onClick={() => void schreiben()}
        >
          {laeuft === 'marker' ? 'wird beauftragt …'
            : gate.markerProbelauf ? 'Markerprojektion probelaufen'
            : 'Marker in Revit schreiben'}
        </button>
        ) : undefined}
      />

      {meldung && (
        <div className="hinweis" style={{ marginBottom: 14 }}>{meldung}</div>
      )}

      {/* ── Das Gate ─────────────────────────────────────────────────── */}

      <div className={gate.bereit ? 'hinweis' : 'hinweis fehler'}
           style={{ marginBottom: 18 }}>
        {gate.bereit ? (
          <>
            <IconOk size={14} /> <strong>{gate.status}</strong> — jede
            technische Beobachtung ist geklärt, jedes Bauteil hat einen
            Zustand, beide Küchenmodelle sind gelesen.
          </>
        ) : (
          <>
            <IconWarnung size={14} /> <strong>Es wird nicht geschrieben.</strong>
            <ul style={{ margin: '8px 0 0 18px' }}>
              {gate.blocker.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </>
        )}
      </div>

      {/* ── Der Probelauf ───────────────────────────────────────────── */}
      {/*
        Ein eigener Kasten und kein Häkchen in einer Zeile: Ihn abzuschalten
        heisst, dass der nächste Lauf in das Hostmodell schreibt. Wer das tut,
        soll es getan haben.
      */}
      <div className={gate.markerProbelauf ? 'hinweis' : 'hinweis warnung'}
           style={{ marginBottom: 14 }}>
        {gate.markerProbelauf ? (
          <>
            <IconOk size={14} /> <strong>Probelauf</strong> — der nächste Lauf
            141 legt die Marker an, meldet was entstehen würde, und rollt
            zurück. Im Modell ändert sich nichts.
          </>
        ) : (
          <>
            <IconWarnung size={14} /> <strong>Schreiblauf</strong> — der
            nächste Lauf 141 schreibt in das Hostmodell.
          </>
        )}
        {' '}
        <button
          type="button"
          className="m-knopf"
          disabled={laeuft !== null}
          onClick={() => void probelaufUmschalten()}
        >
          {laeuft === 'probelauf' ? 'wird gesetzt …'
            : gate.markerProbelauf ? 'auf Schreiblauf umstellen'
            : 'wieder auf Probelauf stellen'}
        </button>
      </div>

      {gate.hinweise.length > 0 && (
        <div className="hinweis" style={{ marginBottom: 18 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {gate.hinweise.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </div>
      )}

      {/* ── Die Zahlen ───────────────────────────────────────────────── */}

      <h3>Modellbestand</h3>
      <div className="kennzahlen" style={{ marginBottom: 18 }}>
        <Zahl titel="Küchenmodelle freigegeben"
              wert={`${gate.quellmodelleFreigegeben} / ${gate.quellmodelleErwartet}`}
              warnung={gate.quellmodelleFreigegeben !== gate.quellmodelleErwartet} />
        <Zahl titel="gelesen" wert={gate.quellmodelleGelesen} />
        <Zahl titel="nicht geladen" wert={gate.quellmodelleUngeladen}
              warnung={gate.quellmodelleUngeladen > 0} />
        <Zahl titel="Bauteile" wert={gate.bauteile} />
        <Zahl titel="noch ungeprüft" wert={gate.bauteileUngeprueft}
              warnung={gate.bauteileUngeprueft > 0} />
        <Zahl titel="Lüftungsdecken" wert={gate.lueftungsdecken} />
        <Zahl titel="Leuchten" wert={gate.leuchten} />
      </div>

      <h3>Zuordnung</h3>
      <div className="kennzahlen" style={{ marginBottom: 18 }}>
        <Zahl titel="Deckung" wert={`${gate.deckungProzent} %`}
              warnung={gate.deckungProzent < 100} />
        <Zahl titel="technische Beobachtungen" wert={gate.beobachtungenTechnisch} />
        <Zahl titel="offen" wert={gate.beobachtungenOffen}
              warnung={gate.beobachtungenOffen > 0} />
        <Zahl titel="zugeordnet" wert={gate.beobachtungenZugeordnet} />
        <Zahl titel="ausgeschlossen" wert={gate.beobachtungenAusgeschlossen} />
        <Zahl titel="ohne Ziel freigegeben" wert={gate.beobachtungenOhneZiel} />
        <Zahl titel="Mehrfachzuordnungen" wert={gate.mehrfachzuordnungen}
              warnung={gate.mehrfachzuordnungen > 0} />
        <Zahl titel="Pläne ohne Registrierung" wert={gate.plaeneOhneRegistrierung}
              warnung={gate.plaeneOhneRegistrierung > 0} />
      </div>

      {/* ── Die Leuchten ─────────────────────────────────────────────── */}

      <div className="bereich-kopf">
        <h3><IconElektro size={16} /> Leuchten und Elektroplanung</h3>
        <button className="zweitrangig" disabled={laeuft !== null}
                onClick={() => void abgleichen()}>
          {laeuft === 'leuchten' ? 'gleicht ab …' : 'Leuchten abgleichen'}
        </button>
      </div>

      <p className="erlaeuterung">
        Eine Leuchte in der Küchenlüftungsdecke ist zweierlei: Bestandteil der
        Decke und Verbraucher an einem Stromkreis. Sie steht deshalb
        ausdrücklich <strong>nicht</strong> in den Elektrobauteilen — dort läge
        sie neben 5.500 Deckenleuchten ein zweites Mal, und die
        Anschlussleistung stünde zweimal in der Bilanz.
      </p>

      {leuchten && leuchten.gesamt > 0 ? (
        <>
          <div className="kennzahlen" style={{ marginBottom: 14 }}>
            {Object.entries(leuchten.jeStatus).map(([k, v]) => (
              <Zahl key={k} titel={STATUS[k] ?? k} wert={v}
                    warnung={k === 'FEHLT_IN_ELEKTRO' || k === 'MEHRDEUTIG'} />
            ))}
          </div>

          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Ebene</th>
                <th>Typ</th>
                <th>Bezug</th>
                <th style={{ textAlign: 'right' }}>Abstand</th>
                <th style={{ textAlign: 'right' }}>Leistung</th>
                <th>Befund</th>
              </tr>
            </thead>
            <tbody>
              {leuchten.leuchten.slice(0, 200).map((l) => (
                <tr key={l.id}>
                  <td>{l.etage}</td>
                  <td>{l.kucaTyp ?? l.kucaFamilie ?? '—'}</td>
                  <td>
                    <span className={
                      l.bezugsstatus === 'ZUGEORDNET' ? 'marke gut'
                        : l.bezugsstatus === 'FEHLT_IN_ELEKTRO' ? 'marke warnung'
                          : 'marke neutral'}>
                      {STATUS[l.bezugsstatus] ?? l.bezugsstatus}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {l.abstandM !== null ? `${l.abstandM.toFixed(2)} m` : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {l.leistungW !== null ? `${l.leistungW} W` : '—'}
                  </td>
                  <td className="sehr-schwach">{l.begruendung}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {leuchten.gesamt > 200 && (
            <p className="sehr-schwach">
              … und {leuchten.gesamt - 200} weitere.
            </p>
          )}
        </>
      ) : (
        <div className="hinweis">
          Noch kein Leuchtenabgleich. Er setzt voraus, dass die Küchenmodelle
          inventarisiert sind und die Kollisionsaufnahme gelaufen ist — die
          Lage der Elektrobauteile kommt von dort, nicht aus dem
          Elektrobestand selbst.
        </div>
      )}
    </div>
  );
}

const STATUS: Record<string, string> = {
  ZUGEORDNET: 'zugeordnet',
  FEHLT_IN_ELEKTRO: 'fehlt in Elektro',
  MEHRDEUTIG: 'mehrdeutig',
  OHNE_KOORDINATE: 'ohne Koordinate',
  OFFEN: 'offen',
  KEIN_BEZUG: 'kein Bezug',
};

function Zahl({ titel, wert, warnung }: {
  titel: string; wert: number | string; warnung?: boolean;
}) {
  const kritisch = warnung === true
    && (typeof wert === 'number' ? wert > 0 : true);

  return (
    <div className="kennzahl">
      <div className="kennzahl-wert"
           style={kritisch ? { color: 'var(--warnung)' } : undefined}>
        {wert}
      </div>
      <div className="kennzahl-name">{titel}</div>
    </div>
  );
}
